import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../components/AuthContext";
import { useParams } from "react-router-dom";
import api from "../api/api";
import {
  Paper, Typography, Grid, Card, CardContent, Chip, Box,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Avatar, Divider, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip
} from "@mui/material";
import PhotoCameraIcon   from "@mui/icons-material/PhotoCamera";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Chart from "react-apexcharts";
import DownloadIcon from "@mui/icons-material/Download";
import ArticleIcon from "@mui/icons-material/Article";

const baseURL = process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === "production" ? "" : "http://127.0.0.1:8000");

const COLORS = {
  vigente:        "#2E7D32",
  pendiente:      "#E65100",
  vencido:        "#C62828",
  desactualizado: "#757575",
};

const EstadoChip = ({ estado }) => {
  const map = {
    vigente:        { label: "Current",     bg: COLORS.vigente },
    pendiente:      { label: "Pending",     bg: COLORS.pendiente },
    vencido:        { label: "Expired",     bg: COLORS.vencido },
    desactualizado: { label: "Outdated",    bg: COLORS.desactualizado },
  };
  const cfg = map[estado] ?? { label: estado, bg: "#555" };
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: "white", fontWeight: 600 }} />;
};

const KpiCard = ({ title, value, color }) => (
  <Card sx={{ borderLeft: `5px solid ${color}`, height: "100%" }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h4" sx={{ color, fontWeight: 700 }}>{value}</Typography>
    </CardContent>
  </Card>
);

function initiales(nombre) {
  if (!nombre) return "?";
  return nombre.split(" ").slice(0, 2).map(p => p[0]).join("").toUpperCase();
}

export default function PerfilEmpleado() {
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const puedeEditarFoto = hasPermission("EMPLEADOS_EDITAR");
  const [data, setData] = useState(null);
  const [fichasModal, setFichasModal] = useState(null); // { version, fichas[] }
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const fotoInputRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get(`/perfil_empleado/${id}`);
      setData(res.data);
    } catch {
      alert("Error loading employee profile.");
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const formData = new FormData();
    formData.append("file", file);
    setUploadingFoto(true);
    try {
      await api.post(`/empleados/${id}/foto`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      cargar();
    } catch {
      alert("Error uploading photo. Please check that it's a valid image (JPG, PNG, or WEBP).");
    } finally {
      setUploadingFoto(false);
    }
  };

  if (!data) return <Typography sx={{ p: 3 }}>Loading profile...</Typography>;
  if (data.error) return <Typography sx={{ p: 3 }} color="error">{data.error}</Typography>;

  const {
    info, vigentes, pendientes, vencidos, desactualizados,
    vencen_30, historial, pendientes_detalle, total_horas
  } = data;

  const desactualizadosIds = new Set(desactualizados.map(d => d.id_curso));

  const total = vigentes.length + pendientes.length + vencidos.length + desactualizados.length;
  const pct   = total ? Math.round(vigentes.length * 100 / total) : 0;

  const pieSeries  = [vigentes.length, pendientes.length, vencidos.length, desactualizados.length];
  const hayPie     = pieSeries.some(v => v > 0);

  return (
    <Paper sx={{ p: 3 }}>

      {/* ── Header ────────────────────────────────────────── */}
      <Card sx={{ mb: 3, background: "#FAFAFA", border: "1px solid #ddd" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Tooltip title={puedeEditarFoto ? "Change photo" : ""}>
              <Box
                sx={{ position: "relative", display: "inline-block", cursor: puedeEditarFoto ? "pointer" : "default" }}
                onClick={() => puedeEditarFoto && fotoInputRef.current?.click()}
              >
                <Avatar
                  src={info.foto_url ? `${baseURL}/${info.foto_url}` : undefined}
                  sx={{ width: 80, height: 80, bgcolor: "#C62828", fontSize: 28, fontWeight: 700 }}
                >
                  {initiales(info.nombre_completo)}
                </Avatar>
                {puedeEditarFoto && (
                  <Box sx={{
                    position: "absolute", bottom: 0, right: 0,
                    bgcolor: "rgba(0,0,0,0.55)", borderRadius: "50%",
                    width: 28, height: 28,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {uploadingFoto
                      ? <CircularProgress size={14} sx={{ color: "white" }} />
                      : <PhotoCameraIcon sx={{ fontSize: 16, color: "white" }} />
                    }
                  </Box>
                )}
              </Box>
            </Tooltip>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              ref={fotoInputRef}
              style={{ display: "none" }}
              onChange={handleFotoUpload}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>{info.nombre_completo}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="subtitle1" sx={{ color: "#C62828", fontWeight: 600 }}>
                  {info.puesto_texto || "No position"}
                </Typography>
                {info.puesto_doc_url && (
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label="Position description"
                    size="small"
                    color="error"
                    variant="outlined"
                    clickable
                    component="a"
                    href={`${baseURL}/${info.puesto_doc_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                {info.area_texto && <Chip label={info.area_texto} size="small" variant="outlined" />}
                {info.departamento_texto && <Chip label={info.departamento_texto} size="small" variant="outlined" />}
                <Chip
                  label={info.estatus === "activo" ? "Active" : "Inactive"}
                  size="small"
                  sx={{ bgcolor: info.estatus === "activo" ? COLORS.vigente : "#555", color: "white" }}
                />
              </Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" color="text.secondary">Compliance</Typography>
              <Typography variant="h3" fontWeight={700} sx={{ color: pct >= 80 ? COLORS.vigente : COLORS.vencido }}>
                {pct}%
              </Typography>
              <Typography variant="caption" color="text.secondary">{vigentes.length} of {total} courses</Typography>
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #ddd" }}>
                <Typography variant="caption" color="text.secondary">Training hours</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: "#1565c0" }}>
                  {total_horas % 1 === 0 ? total_horas : total_horas.toFixed(1)} hrs
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ── KPIs ──────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={true}><KpiCard title="Current"          value={vigentes.length}          color={COLORS.vigente} /></Grid>
        <Grid item xs={6} sm={4} md={true}><KpiCard title="Pending"         value={pendientes.length}        color={COLORS.pendiente} /></Grid>
        <Grid item xs={6} sm={4} md={true}><KpiCard title="Expired"         value={vencidos.length}          color={COLORS.vencido} /></Grid>
        <Grid item xs={6} sm={4} md={true}><KpiCard title="Outdated"        value={desactualizados.length}   color={COLORS.desactualizado} /></Grid>
        <Grid item xs={6} sm={4} md={true}><KpiCard title="Next 30 days"    value={vencen_30.length}         color="#6A1B9A" /></Grid>
      </Grid>

      {/* ── Donut + pending courses ─────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Overall status</Typography>
            {hayPie ? (
              <Chart
                type="donut"
                height={260}
                series={pieSeries}
                options={{
                  labels: ["Current", "Pending", "Expired", "Outdated"],
                  colors: [COLORS.vigente, COLORS.pendiente, COLORS.vencido, COLORS.desactualizado],
                  legend: { position: "bottom" },
                  plotOptions: { pie: { donut: { size: "55%" } } },
                  dataLabels: { enabled: true },
                }}
              />
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No courses assigned
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Pending courses ({pendientes_detalle.length})
            </Typography>
            {pendientes_detalle.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No pending courses
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#111" }}>
                    <TableCell sx={{ color: "white" }}>Course</TableCell>
                    <TableCell sx={{ color: "white" }}>Type</TableCell>
                    <TableCell sx={{ color: "white" }} align="center">Mandatory</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientes_detalle.map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{c.nombre_curso}</TableCell>
                      <TableCell>
                        <Chip label={c.tipo === "puesto" ? "By Position" : c.tipo === "general" ? "General" : c.tipo === "opcional" ? "Optional" : c.tipo} size="small" variant="outlined"
                          color={c.tipo === "puesto" ? "primary" : c.tipo === "general" ? "success" : "default"} />
                      </TableCell>
                      <TableCell align="center">
                        {c.obligatorio
                          ? <Chip label="Yes" size="small" sx={{ bgcolor: COLORS.vencido, color: "white" }} />
                          : <Chip label="No" size="small" variant="outlined" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Upcoming expirations ───────────────────────────── */}
      {vencen_30.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, color: "#6A1B9A" }}>
            Upcoming expirations — 30 days ({vencen_30.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#111" }}>
                <TableCell sx={{ color: "white" }}>Course</TableCell>
                <TableCell sx={{ color: "white" }}>Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vencen_30.map((v, i) => (
                <TableRow key={i} hover>
                  <TableCell>{v.nombre_curso}</TableCell>
                  <TableCell>{v.fecha_vencimiento}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── Training history ───────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Training history ({historial.length})
        </Typography>
        {historial.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No completed training
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "#111" }}>
                <TableCell sx={{ color: "white" }}>Course</TableCell>
                <TableCell sx={{ color: "white" }}>Version</TableCell>
                <TableCell sx={{ color: "white" }}>Date</TableCell>
                <TableCell sx={{ color: "white" }}>Result</TableCell>
                <TableCell sx={{ color: "white" }}>Expiration</TableCell>
                <TableCell sx={{ color: "white" }} align="center">Mandatory</TableCell>
                <TableCell sx={{ color: "white" }} align="center">Evidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historial.map(h => (
                <TableRow key={h.id_curso_empleado} hover>
                  <TableCell>{h.nombre_curso}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span>{h.numero_version ?? "—"}</span>
                      {h.fichas?.length > 0 && (
                        <Tooltip title={`${h.fichas.length} working sheet(s) changed`}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setFichasModal({ version: h.numero_version, fichas: h.fichas })}
                          >
                            <ArticleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{h.fecha_capacitacion}</TableCell>
                  <TableCell>
                    {h.resultado
                      ? <Chip label={h.resultado === "aprobado" ? "Passed" : "Not passed"} size="small"
                          sx={{ bgcolor: h.resultado === "aprobado" ? COLORS.vigente : COLORS.vencido, color: "white" }} />
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {h.fecha_vencimiento
                      ? <Box sx={{ color: new Date(h.fecha_vencimiento) < new Date() ? COLORS.vencido : "inherit" }}>
                          {h.fecha_vencimiento}
                        </Box>
                      : desactualizadosIds.has(h.id_curso)
                        ? <Chip label="Outdated" size="small"
                            sx={{ bgcolor: COLORS.desactualizado, color: "white", fontSize: "11px" }} />
                        : <span style={{ color: "#888", fontStyle: "italic" }}>Until new version</span>}
                  </TableCell>
                  <TableCell align="center">
                    {h.obligatorio
                      ? <Chip label="Yes" size="small" sx={{ bgcolor: COLORS.vencido, color: "white", fontSize: "11px" }} />
                      : <Chip label="No" size="small" variant="outlined" sx={{ fontSize: "11px" }} />}
                  </TableCell>
                  <TableCell align="center">
                    {h.evidencia_url ? (
                      <a href={`${baseURL}/${h.evidencia_url.replace(/\\/g, "/")}`} target="_blank" rel="noreferrer">
                        <IconButton size="small"><DownloadIcon /></IconButton>
                      </a>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Working sheet history modal */}
      <Dialog open={!!fichasModal} onClose={() => setFichasModal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Working sheets changed — version {fichasModal?.version}
        </DialogTitle>
        <DialogContent dividers>
          {fichasModal?.fichas.map(f => (
            <Box
              key={f.codigo}
              sx={{
                display: "flex", flexDirection: "column",
                borderBottom: "1px solid", borderColor: "divider",
                py: 1.5, "&:last-child": { borderBottom: 0 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography variant="body2" fontWeight={700}>{f.codigo}</Typography>
                {f.version && (
                  <Chip label={`Rev. ${f.version}`} size="small" color="primary" variant="outlined"
                    sx={{ fontSize: "10px", height: 18, "& .MuiChip-label": { px: 0.8 } }} />
                )}
                {f.archivo_url && (
                  <Chip
                    icon={<PictureAsPdfIcon />}
                    label="View PDF"
                    size="small"
                    color="error"
                    variant="outlined"
                    clickable
                    component="a"
                    href={`${baseURL}/${f.archivo_url.replace(/^fichas\//, "fichas_archivos/")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ ml: "auto" }}
                  />
                )}
              </Box>
              {f.nombre && f.nombre !== f.codigo && (
                <Typography variant="caption" color="text.secondary">{f.nombre}</Typography>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFichasModal(null)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
}
